'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import TermsAndConditionsModal from './TermsAndconditionsModel';
import { Link } from '@/i18n/navigation';
import { usePermissions } from '@/contexts/PermissionsContext';
import eramFooterLogo from '@/assets/imgs/eram-footer-logo.jpg';
import footerEllipse from '@/assets/illustrations/footer-ellipse.svg';
import facebookIcon from '@/assets/icons/facebook-icon.svg';
import linkedinIcon from '@/assets/icons/linkedin-icon.svg';
import tiktokIcon from '@/assets/icons/tiktok-icon.svg';
import instagramIcon from '@/assets/icons/instagram-icon.svg';
import xIcon from '@/assets/icons/x-icon.svg';

export default function Footer() {
  const tNavigation = useTranslations('navigation');
  const tFooter = useTranslations('footer');
  const { isSectionVisibleInFooter } = usePermissions();
  const [openTermsAndConditionsModal, setOpenTermsAndConditionsModal] = useState(false);

  return (
    <footer className="relative" role="contentinfo" aria-label="Site footer">
      <div className="relative mx-auto flex min-h-[300px] flex-col items-center justify-between gap-6 px-4 pt-16 pb-6 sm:min-h-[400px] sm:gap-8 sm:px-6 sm:pt-24 sm:pb-8 lg:min-h-[455px] lg:gap-10 lg:px-0 lg:pt-32 lg:pb-10">
        <Image
          src={footerEllipse}
          alt=""
          className="absolute bottom-0 -z-10 h-full w-full object-cover"
          priority={false}
          loading="lazy"
          sizes="100vw"
          aria-hidden="true"
        />

        <div className="max-w-laptop mx-auto flex w-full flex-col items-end justify-between gap-6 px-4 sm:px-6 lg:px-8">
          {/* Logo and Description */}
          <div className="flex w-full flex-col items-center justify-between gap-6 sm:flex-row sm:items-start">
            <div className="flex flex-col gap-2 self-start sm:gap-3 sm:self-auto">
              <Link href="/" aria-label="Go to homepage">
                <Image
                  src={eramFooterLogo}
                  alt="Eram Group Logo"
                  className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12"
                  priority={true}
                  sizes="(max-width: 640px) 32px, (max-width: 1024px) 40px, 48px"
                />
              </Link>
              <p className="max-w-48 text-sm text-white">{tFooter('yourTrustedPartner')}</p>
            </div>

            <div className="mt-auto">
              {/* Navigation Links */}
              <nav className="" aria-label="Footer navigation">
                <ul
                  className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:justify-start lg:gap-6"
                  role="list">
                  {isSectionVisibleInFooter('companies') && (
                    <li role="listitem">
                      <Link
                        href="#companies"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('companies')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('aboutUs') && (
                    <li role="listitem">
                      <Link
                        href="#about-us"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('aboutUs')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('ourValues') && (
                    <li role="listitem">
                      <Link
                        href="#our-values"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('ourValues')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('objective') && (
                    <li role="listitem">
                      <Link
                        href="#objective"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('objective')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('motion') && (
                    <li role="listitem">
                      <Link
                        href="#motion"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('motion')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('news') && (
                    <li role="listitem">
                      <Link
                        href="#news"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('news')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('ourClients') && (
                    <li role="listitem">
                      <Link
                        href="#our-clients"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('ourClients')}
                      </Link>
                    </li>
                  )}
                  {isSectionVisibleInFooter('contactUs') && (
                    <li role="listitem">
                      <Link
                        href="#contact-us"
                        className="text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                        {tNavigation('contactUs')}
                      </Link>
                    </li>
                  )}
                  <li role="listitem">
                    <div
                      onClick={() => setOpenTermsAndConditionsModal(!openTermsAndConditionsModal)}
                      className="cursor-pointer text-xs font-medium text-white transition-colors hover:text-gray-300 sm:text-sm">
                      {tNavigation('termsAndConditions')}
                    </div>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Social Media Icons */}
            <div
              className="flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-4.5"
              role="group"
              aria-label="Social media links">
              <Link
                href="https://www.facebook.com/Eramgroup.Official/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                className="text-white">
                <Image src={facebookIcon} alt="Facebook" className="size-5 sm:size-6 lg:size-7" />
              </Link>
              <Link
                href="https://www.linkedin.com/company/99168561/admin/dashboard/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on LinkedIn"
                className="text-white">
                <Image src={linkedinIcon} alt="LinkedIn" className="size-5 sm:size-6 lg:size-7" />
              </Link>
              <Link
                href="https://www.tiktok.com/@eram.group?lang=en"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on TikTok"
                className="text-white">
                <Image src={tiktokIcon} alt="TikTok" className="size-5 sm:size-6 lg:size-7" />
              </Link>
              <Link
                href="https://www.instagram.com/eram_group_sa/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
                className="text-white">
                <Image src={instagramIcon} alt="Instagram" className="size-5 sm:size-6 lg:size-7" />
              </Link>
              <Link
                href="https://x.com/eramgroup_sa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on X"
                className="text-white">
                <Image src={xIcon} alt="X" className="size-5 sm:size-6 lg:size-7" />
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="relative z-10 text-center">
          <p className="text-xs text-white sm:text-sm">{tFooter('copyright')}</p>
        </div>
      </div>
      {openTermsAndConditionsModal && (
        <TermsAndConditionsModal
          isOpen={true}
          onClose={() => {
            setOpenTermsAndConditionsModal(false);
          }}
        />
      )}
    </footer>
  );
}
